<template>
  <v-form ref="permanentPlacementForm" v-model="isFormValid" lazy-validation>
    <v-tabs v-model="tab">
      <v-tab :key="PERMANENT_PLACEMENT">
        Contract Details
      </v-tab>
      <v-tab :key="COMMISSIONS">
        Commissions
      </v-tab>
      <v-tab :key="EXPENSES">
        Expenses
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item :key="PERMANENT_PLACEMENT">
        <v-row class="mt-3">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              label="Job Title"
              v-model="jobTitle"
              :readonly="readonly"
              :rules="[required('Job Title is Required')]"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Placement Date"
              :value="permPlacementDate"
              :isRequired="true"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementDate = newValue"
            ></SimpleDatePicker>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Invoice Date"
              :value="permPlacementInvoiceDate"
              :isRequired="false"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementInvoiceDate = newValue"
            ></SimpleDatePicker>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Invoice Due Date"
              :value="permPlacementInvoiceDueDate"
              :isRequired="false"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementInvoiceDueDate = newValue"
            ></SimpleDatePicker>
          </v-col>
        </v-row>
        <v-row class="mt-3 mb-5">
          <v-col cols="12" sm="6" md="4">
            <FormattedNumberField
              label="Salary"
              :value="permPlacementSalary"
              :required="true"
              :readonly="readonly"
              prepend-inner-icon="attach_money"
              @valueChange="(newValue) => this.permPlacementSalary = newValue"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="8" sm="4" md="2">
            <FormattedNumberField
              label="Fee"
              :value="permPlacementFee"
              :rules="[isValidFee]"
              :readonly="readonly"
              :prepend-inner-icon="permPlacementFeeTypeId === FIXED ?'attach_money' : null"
              :validate-on-blur="true"
              @valueChange="feeChange"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="4" sm="2" md="1" class="pl-0 pt-0">
            <v-radio-group
              v-model="permPlacementFeeTypeId"
              :readonly="readonly"
              @change="feeTypeIdChange"
            >
              <v-radio
                label="%"
                :value="PERCENTAGE"
              ></v-radio>
              <v-radio
                label="Fixed"
                :value="FIXED"
              ></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <FormattedNumberField
              label="Net Fee"
              :value="netFee"
              :readonly="true"
              prepend-inner-icon="attach_money"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <FormattedNumberField
              label="Gross Profit"
              :value="grossProfit"
              :readonly="true"
              prepend-inner-icon="attach_money"
              :hint="grossProfitHint"
              :persistent-hint="!!grossProfitHint"
            ></FormattedNumberField>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="COMMISSIONS">
        <v-row class="my-5">
          <v-col cols="12" class="px-10">
            <CommissionsCollector
              ref="commissionCollector"
              commissionComponent="CommissionInput"
              commissionType="commission"
              :commissions="commissions"
              :readonly="readonly"
              class="mb-2"
              @commissionsChange="handleCommissionsChange"
            ></CommissionsCollector>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="EXPENSES">
        <v-row class="my-5">
          <v-col cols="12" class="px-10">
            <ExpensesCard
              class="px-2"
              cardTitle="Expenses"
              :expensesList="expenses"
              :readonly="readonly"
              :showActive="false"
              :showExpenseAmountType="false"
              @expensesListChange="(newValue) => this.expenses = newValue"
            ></ExpensesCard>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </v-form>
</template>

<script>
import { required } from '@/lib/formValidations'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { CommissionsCollector } from '@/components/commissions'
import { ExpensesCard } from '@/components/expenses'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { PERCENTAGE, FIXED } from '@/lib/constants/permPlacementFeeTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { getTotalCommisions } from '@/lib/commissions'
import { PRE_GP } from '@/lib/constants/expensesTypes'
import { formatCurrency } from '@/lib/display'
import { FormattedNumberField } from '@/components/shared/textFields'

export default {
  props: [
    'readonly',
    'candidateId'
  ],
  components: {
    SimpleDatePicker,
    CommissionsCollector,
    ExpensesCard,
    FormattedNumberField
  },
  data () {
    return {
      tab: null,
      isFormValid: null,
      PERCENTAGE,
      FIXED,
      CREATE_FROM_ZOHO,
      PERMANENT_PLACEMENT: 1,
      COMMISSIONS: 2,
      EXPENSES: 3,
      expensesDeduction: 0,
      expensesDeductionHint: ''
    }
  },
  computed: {
    ...mapFields('dealV2', ['deal']),
    ...mapFields('dealClient', ['existingClientId', 'directPlacementsExpenses']),

    currentAssignment () {
      if (
        !this.deal ||
        !this.deal.candidates ||
        !Array.isArray(this.deal.candidates)
      ) {
        return null
      }

      const candidate = this.deal.candidates.find(
        c => c.id === this.candidateId
      )

      return candidate ? candidate.dealAssignment : null
    },

    jobTitle: {
      get () { return this.currentAssignment ? this.currentAssignment.jobTitle : '' },
      set (value) { this.$emit('setJobTitle', value, this.candidateId) }
    },
    permPlacementDate: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementDate : null },
      set (value) { this.$emit('setPermPlacementDate', value, this.candidateId) }
    },
    permPlacementSalary: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementSalary : null },
      set (value) { this.$emit('setPermPlacementSalary', value, this.candidateId) }
    },
    permPlacementFee: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementFee : null },
      set (value) { this.$emit('setPermPlacementFee', value, this.candidateId) }
    },
    permPlacementFeeTypeId: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementFeeTypeId : null },
      set (value) { this.$emit('setPermPlacementFeeTypeId', value, this.candidateId) }
    },
    permPlacementInvoiceDate: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementInvoiceDate : null },
      set (value) { this.$emit('setPermPlacementInvoiceDate', value, this.candidateId) }
    },
    permPlacementInvoiceDueDate: {
      get () { return this.currentAssignment ? this.currentAssignment.permPlacementInvoiceDueDate : null },
      set (value) { this.$emit('setPermPlacementInvoiceDueDate', value, this.candidateId) }
    },
    commissions: {
      get () { return this.currentAssignment ? this.currentAssignment.commissions : [] },
      set (value) { this.$emit('setCommissions', value, this.candidateId) }
    },
    expenses: {
      get () { return this.currentAssignment ? this.currentAssignment.expenses : [] },
      set (value) { this.$emit('setExpenses', value, this.candidateId) }
    },

    netFee () {
      return this.permPlacementSalary && this.permPlacementFee && this.permPlacementFeeTypeId
        ? (this.permPlacementFeeTypeId === PERCENTAGE
          ? (this.permPlacementSalary * this.permPlacementFee) / 100 : this.permPlacementFee)
        : null
    },
    grossProfit () {
      if (this.netFee !== null) {
        return (this.netFee - this.expensesDeduction).toFixed(2)
      } else {
        return null
      }
    },
    grossProfitHint () {
      if (this.netFee !== null) {
        let hint = `Gross Profit = Net Fee (${formatCurrency(this.netFee)})`
        return this.expensesDeduction > 0 ? `${hint} - ${this.expensesDeductionHint}` : hint
      } else {
        return null
      }
    }
  },
  watch: {
    directPlacementsExpenses () {
      if (this.expenses && this.expenses.length > 0) {
        const defaultExpenses = this.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (const defaultExpense of defaultExpenses) {
          const index = this.expenses.findIndex(e => e.id === defaultExpense.id && !!e.clientDefaultExpense)
          this.expenses.splice(index, 1)
        }
      }

      if (this.directPlacementsExpenses && this.directPlacementsExpenses.length > 0) {
        for (let i = this.directPlacementsExpenses.length - 1; i >= 0; i--) {
          this.expenses.unshift({ ...this.directPlacementsExpenses[i], clientDefaultExpense: true })
        }
      }
    },
    expenses () {
      this.expensesDeduction = 0
      this.expensesDeductionHint = ''
      this.expenses.forEach(expense => {
        if (expense.expenseTypeId === PRE_GP) {
          this.expensesDeduction += parseFloat(expense.amount)
          this.expensesDeductionHint += `${expense.expenseName} (${formatCurrency(expense.amount)}) - `
        }
      })
      this.expensesDeductionHint = this.expensesDeductionHint.slice(0, -3)
    }
  },
  methods: {
    ...mapActions('dealAssignment', ['saveDealAssignment']),
    getTotalCommisions,
    required,
    checkRequiredFields () {
      return this.jobTitle &&
        this.permPlacementDate &&
        this.permPlacementSalary &&
        this.permPlacementFee &&
        this.permPlacementFeeTypeId
    },
    isValidFee () {
      return this.permPlacementFee && this.permPlacementFeeTypeId
        ? (this.permPlacementFeeTypeId === PERCENTAGE
          ? (parseInt(this.permPlacementFee) <= 100 ? true : 'The fee percent is not valid.') : true)
        : 'Fee is Required'
    },
    handleCommissionsChange (value) {
      this.commissions = value
    },
    feeChange (newValue) {
      this.permPlacementFee = newValue
      if (this.permPlacementFee && !this.permPlacementFeeTypeId) {
        this.permPlacementFee > 100 ? this.permPlacementFeeTypeId = FIXED : this.permPlacementFeeTypeId = PERCENTAGE
      }
    },
    feeTypeIdChange () {
      if (this.permPlacementFee && this.permPlacementFee > 100 && this.permPlacementFeeTypeId === PERCENTAGE) {
        this.permPlacementFee = null
      }
      this.$refs.permanentPlacementForm.resetValidation()
    },
    isPermanentPlacementValid () {
      return this.$refs.permanentPlacementForm.validate() ? true : 'Please fill out the required fields to proceed'
    }
  }
}
</script>
